/**
 * DashboardHome — HOME = ACTION HUB
 * Prowadzenie użytkownika do efektu: user → post → publikacja → reakcja
 * 4 sekcje: STATUS + GŁÓWNE CTA + CO TERAZ + FEEDBACK
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Home as HomeIcon, Check, ChevronRight, Rocket,
  Loader2, Inbox, ArrowRight, Sparkles, Mail,
  Clock, Zap, Users, CheckCircle2, Star,
  MessageCircle, Send, User, BarChart3, TrendingUp, TrendingDown, Eye, Lightbulb, Globe2,
  Dog, Plus, ExternalLink, AlertCircle, Target, Copy, Camera, Wand2, EyeOff
} from 'lucide-react';
import PanelLayout from '@/components/PanelLayout';
import MarketplaceLeadsWidget from '@/components/MarketplaceLeadsWidget';
import FirstLitterPromo from '@/components/FirstLitterPromo';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useStartMode } from '@/hooks/useStartMode';
import { API } from '@/utils/api';
import { trackEvent } from '@/utils/tracking';

/* ═══ FIRST LEAD CELEBRATION ═══ */
function FirstLeadCelebration({ onDismiss }) {
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={onDismiss}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
          className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 text-center shadow-2xl"
          onClick={e => e.stopPropagation()}
          data-testid="first-lead-celebration"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#F47A3B] to-[#F9A66C] flex items-center justify-center shadow-lg shadow-[#F47A3B]/30"
          >
            <Star className="w-10 h-10 text-white" fill="white" />
          </motion.div>
          <h2 className="text-2xl font-extrabold text-zinc-900 mb-2">Masz pierwsze zapytanie!</h2>
          <p className="text-zinc-500 text-[15px] mb-6">Masz nowe zapytanie — klient właśnie do Ciebie napisał.</p>
          <Button
            onClick={onDismiss}
            className="w-full bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-xl py-3 text-[15px] font-bold shadow-lg shadow-[#F47A3B]/25"
            data-testid="first-lead-see-btn"
          >
            Zobacz zapytanie
          </Button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ═══ FLOW STEP ═══ */
function FlowStep({ icon: Icon, label, done, active, isLast }) {
  return (
    <div className="flex items-center">
      <div className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${
        done ? 'bg-emerald-50' : active ? 'bg-[#F47A3B]/10' : 'bg-zinc-50'
      }`}>
        {done ? (
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        ) : (
          <Icon className={`w-4 h-4 ${active ? 'text-[#F47A3B]' : 'text-zinc-300'}`} />
        )}
        <span className={`text-[12px] font-semibold ${
          done ? 'text-emerald-600' : active ? 'text-[#F47A3B]' : 'text-zinc-400'
        }`}>{label}</span>
      </div>
      {!isLast && (
        <ChevronRight className={`w-4 h-4 mx-1 ${done ? 'text-emerald-300' : 'text-zinc-200'}`} />
      )}
    </div>
  );
}

/* ═══ STATUS METRIC ═══ */
function StatusMetric({ label, value, color = 'zinc' }) {
  const colors = {
    green: 'text-emerald-600 bg-emerald-50',
    orange: 'text-[#F47A3B] bg-[#F47A3B]/10',
    zinc: 'text-zinc-600 bg-zinc-50',
    red: 'text-red-500 bg-red-50',
  };
  return (
    <div className="text-center flex-1">
      <p className={`text-[18px] font-bold ${colors[color]?.split(' ')[0] || 'text-zinc-900'}`}>{value}</p>
      <p className="text-[11px] text-zinc-400 mt-0.5">{label}</p>
    </div>
  );
}

/* ═══ MINI SPARKLINE ═══ */
function MiniSparkline({ data, color = '#F47A3B', h = 40 }) {
  if (!data || data.length === 0) return null;
  const values = data.map(d => d.count);
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const range = max - min || 1;
  const w = 200;
  const pts = values.map((v, i) => {
    const x = 2 + (i / (values.length - 1)) * (w - 4);
    const y = h - 2 - ((v - min) / range) * (h - 4);
    return `${x},${y}`;
  }).join(' ');
  const area = `2,${h - 2} ${pts} ${w - 2},${h - 2}`;
  const id = `sg-${color.replace('#', '')}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full" style={{ height: h }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${id})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ═══ TWOJE WYNIKI ═══ */
function MyTrendsBlock() {
  const [trends, setTrends] = useState(null);
  const [days, setDays] = useState(7);
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API}/panel/my-trends?days=${days}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setTrends(r.data)).catch(() => {});
  }, [days, token]);

  if (!trends) return null;

  const trendIcon = (dir) => {
    if (dir === 'up') return <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />;
    if (dir === 'down') return <TrendingDown className="w-3.5 h-3.5 text-amber-500" />;
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="rounded-2xl border border-zinc-200 bg-white p-5 mb-6"
      data-testid="my-trends-block"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-[14px] font-bold text-zinc-900 flex items-center gap-1.5">
          <BarChart3 className="w-4 h-4 text-[#F47A3B]" /> Twoje wyniki
        </p>
        <div className="flex gap-1">
          <button
            onClick={() => setDays(7)}
            className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition ${days === 7 ? 'bg-[#F47A3B]/10 text-[#F47A3B]' : 'text-zinc-400 hover:bg-zinc-50'}`}
            data-testid="trends-7d"
          >7 dni</button>
          <button
            onClick={() => setDays(30)}
            className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition ${days === 30 ? 'bg-[#F47A3B]/10 text-[#F47A3B]' : 'text-zinc-400 hover:bg-zinc-50'}`}
            data-testid="trends-30d"
          >30 dni</button>
        </div>
      </div>

      {/* Charts grid */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="rounded-xl bg-zinc-50 p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] text-zinc-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Zapytania</span>
            {trendIcon(trends.trends.leads)}
          </div>
          <p className="text-[18px] font-bold text-zinc-900 mb-1">{trends.totals.leads}</p>
          <MiniSparkline data={trends.leads} color="#F47A3B" />
        </div>
        <div className="rounded-xl bg-zinc-50 p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] text-zinc-500 flex items-center gap-1"><Eye className="w-3 h-3" /> Odsłony</span>
            {trendIcon(trends.trends.views)}
          </div>
          <p className="text-[18px] font-bold text-zinc-900 mb-1">{trends.totals.views}</p>
          <MiniSparkline data={trends.views} color="#8b5cf6" />
        </div>
        <div className="rounded-xl bg-zinc-50 p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] text-zinc-500 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Klienci</span>
          </div>
          <p className="text-[18px] font-bold text-zinc-900 mb-1">{trends.totals.clients}</p>
          <MiniSparkline data={trends.clients} color="#10b981" />
        </div>
      </div>

      {/* Insight */}
      <div className="rounded-xl bg-[#F47A3B]/5 border border-[#F47A3B]/10 px-3 py-2" data-testid="trends-insight">
        <p className="text-[12px] text-[#F47A3B] font-medium flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> {trends.insight}
        </p>
      </div>
    </motion.div>
  );
}


/* ═══ WEEKLY REPORT CARD ═══ */
function WeeklyReportCard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API}/report/weekly`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setReport(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  if (loading || !report || !report.has_data) return null;

  const { stats, trends, top_types, best_post, recommendation, learning_note } = report;

  const typeLabels = {
    problem: "Problem klienta", insight: "Insight", test: "Demo/test",
    value: "Wartość/porada", micro_result: "Wynik/case", education: "Edukacja",
    cta: "CTA", offer: "Oferta", social_proof: "Opinie",
    behind_scenes: "Kulisy", engagement: "Angażujący", news: "Nowość",
    summary: "Podsumowanie", group_post: "Post w grupie",
  };

  const TrendBadge = ({ tKey }) => {
    const t = trends?.[tKey];
    if (!t || t.direction === 'flat') return null;
    const isUp = t.direction === 'up';
    return (
      <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${isUp ? 'text-emerald-500' : 'text-amber-500'}`}>
        {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {t.pct}%
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.22 }}
      className="rounded-2xl border border-zinc-200 bg-white p-5 mb-6"
      data-testid="weekly-report-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-[14px] font-bold text-zinc-900 flex items-center gap-1.5">
          <Zap className="w-4 h-4 text-[#F47A3B]" /> Jak działają Twoje posty
        </p>
        <span className="text-[10px] text-zinc-400 font-medium">ostatnie 7 dni</span>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { label: 'Posty', value: stats?.posts_published || 0, key: 'posts_published', icon: Send },
          { label: 'Reakcje', value: stats?.reactions || 0, key: 'reactions', icon: Star },
          { label: 'Komentarze', value: stats?.comments || 0, key: 'comments', icon: MessageCircle },
          { label: 'Wiadomości', value: stats?.messages || 0, key: 'messages', icon: Mail },
        ].map(s => (
          <div key={s.key} className="rounded-xl bg-zinc-50 p-3 text-center" data-testid={`report-stat-${s.key}`}>
            <s.icon className="w-3.5 h-3.5 text-zinc-400 mx-auto mb-1" />
            <p className="text-[18px] font-bold text-zinc-900">{s.value}</p>
            <div className="flex items-center justify-center gap-1 mt-0.5">
              <span className="text-[10px] text-zinc-400">{s.label}</span>
              <TrendBadge tKey={s.key} />
            </div>
          </div>
        ))}
      </div>

      {/* Top types */}
      {top_types && top_types.length > 0 && (
        <div className="mb-3" data-testid="report-top-types">
          <p className="text-[11px] text-zinc-500 font-medium mb-1.5">Najlepiej zadziałały</p>
          <div className="flex flex-wrap gap-1.5">
            {top_types.map(t => (
              <span key={t} className="px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-[11px] font-semibold text-emerald-700">
                {typeLabels[t] || t}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Best post */}
      {best_post && (
        <div className="mb-3 rounded-xl bg-zinc-50 p-3 border-l-3 border-[#F47A3B]" data-testid="report-best-post" style={{borderLeft: '3px solid #F47A3B'}}>
          <p className="text-[10px] text-zinc-400 font-semibold mb-1">NAJLEPSZY POST</p>
          <p className="text-[12px] text-zinc-600 italic leading-relaxed mb-1.5">
            &ldquo;{best_post.text?.slice(0, 120)}...&rdquo;
          </p>
          {best_post.reasons?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {best_post.reasons.map((r, i) => (
                <span key={i} className="text-[10px] text-[#F47A3B] font-medium">→ {r}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recommendation */}
      {recommendation?.changes?.length > 0 && (
        <div className="mb-3 rounded-xl bg-[#F47A3B]/5 border border-[#F47A3B]/15 p-3" data-testid="report-recommendation">
          <p className="text-[11px] text-[#F47A3B] font-bold mb-1.5">Co zmieni się w przyszłym tygodniu</p>
          {recommendation.changes.map((c, i) => (
            <p key={i} className="text-[12px] text-zinc-600 leading-relaxed">→ {c}</p>
          ))}
          {recommendation.explanation && (
            <p className="text-[11px] text-zinc-400 mt-2 italic">{recommendation.explanation}</p>
          )}
        </div>
      )}

      {/* Learning badge */}
      {learning_note && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2" data-testid="report-learning-badge">
          <p className="text-[10px] text-blue-700 font-semibold flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> WYNIKI ROSNĄ
            <span className="font-normal text-blue-500 ml-1">— {learning_note}</span>
          </p>
        </div>
      )}
    </motion.div>
  );
}


/* ═══════════════════════════════════════════
   MAIN DASHBOARD
   ═══════════════════════════════════════════ */
export default function DashboardHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const sm = useStartMode();
  const { t } = useTranslation();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFirstLead, setShowFirstLead] = useState(false);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [profileMissing, setProfileMissing] = useState([]);
  const [testWarnings, setTestWarnings] = useState([]);
  const [systemActivity, setSystemActivity] = useState(null);
  const [salesSummary, setSalesSummary] = useState(null);
  const [fbCampaign, setFbCampaign] = useState(null);
  const [fbPosts, setFbPosts] = useState([]);
  const [lastInspiration, setLastInspiration] = useState(null);
  const [actionInsight, setActionInsight] = useState(null);
  const [quickGenerating, setQuickGenerating] = useState(false);
  const [firstWinStatus, setFirstWinStatus] = useState(null);
  const [publishReminder, setPublishReminder] = useState(null);
  const [breedCount, setBreedCount] = useState(0);
  const [postPreview, setPostPreview] = useState(null);
  const [siteInfo, setSiteInfo] = useState(null);
  const [fbConnected, setFbConnected] = useState(null);
  const [fbPageName, setFbPageName] = useState(null);
  const [fbConnecting, setFbConnecting] = useState(false);
  const [pageStatus, setPageStatus] = useState(null);
  const [hasPhotos, setHasPhotos] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(null);
  const [adsUsage, setAdsUsage] = useState(null);
  const [waSettings, setWaSettings] = useState(null);
  const [waSaving, setWaSaving] = useState(false);
  const [waPhone, setWaPhone] = useState('');
  const [ctaPulse, setCtaPulse] = useState(false);
  const [abVariant] = useState(() => {
    const stored = localStorage.getItem('ab_home_cta_v1');
    if (stored === 'A' || stored === 'B') return stored;
    const v = Math.random() < 0.5 ? 'A' : 'B';
    localStorage.setItem('ab_home_cta_v1', v);
    return v;
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  /* ── Onboarding guard: redirect new users to interview ── */
  useEffect(() => {
    const checkOnboarding = async () => {
      try {
        const res = await axios.get(`${API}/interview/status`, { headers });
        if (res.data?.status !== 'completed') {
          navigate('/onboarding');
        }
      } catch {}
    };
    if (token) checkOnboarding();
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/panel/leads/all`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/profile`, { headers }).catch(() => ({ data: {} })),
      axios.get(`${API}/panel/system-activity`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/panel/leads/sales-summary`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/fb-campaign/active`, { headers }).catch(() => ({ data: { campaign: null, posts: [] } })),
      axios.get(`${API}/inspiration/library`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/breeds`, { headers }).catch(() => ({ data: { breeds: [] } })),
      axios.get(`${API}/kennels`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/business/my`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/booster/campaign-pack`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/page`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/ads/usage`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/whatsapp/settings`, { headers }).catch(() => ({ data: null })),
    ]).then(([leadsRes, profileRes, activityRes, salesRes, campRes, inspoRes, breedsRes, kennelsRes, bizRes, packRes, pageRes, usageRes, waRes]) => {
      setLeads(Array.isArray(leadsRes.data) ? leadsRes.data : leadsRes.data?.leads || []);
      setProfileCompletion(profileRes.data?.progress || 0);
      setProfileMissing(profileRes.data?.missing || []);
      setTestWarnings(profileRes.data?.test_warnings || []);
      setSystemActivity(activityRes.data);
      setSalesSummary(salesRes.data);
      if (campRes.data?.campaign) {
        setFbCampaign(campRes.data.campaign);
        setFbPosts(campRes.data.posts || []);
      }
      const inspoList = Array.isArray(inspoRes.data) ? inspoRes.data : [];
      if (inspoList.length > 0) setLastInspiration(inspoList[0]);
      setBreedCount((breedsRes.data?.breeds || []).length);

      // Post preview — first unpublished post or first post from campaign
      const allPosts = campRes.data?.posts || [];
      const packCards = packRes.data?.cards || [];
      const previewSource = allPosts.find(p => p.status !== 'published') || allPosts[0] || packCards[0];
      if (previewSource?.content) {
        setPostPreview(previewSource.content);
      }

      // FB connection status
      axios.get(`${API}/integrations/facebook/status`, { headers })
        .then(fbRes => {
          setFbConnected(fbRes.data?.is_connected || false);
          setFbPageName(fbRes.data?.selected_page?.name || null);
        })
        .catch(() => setFbConnected(false));

      // Site info — kennel or business slug
      const kennels = Array.isArray(kennelsRes.data) ? kennelsRes.data : [];
      const businesses = Array.isArray(bizRes.data) ? bizRes.data : [];
      if (kennels.length > 0) {
        setSiteInfo({
          name: kennels[0].name,
          slug: kennels[0].slug,
          type: 'hodowla',
          id: kennels[0].id,
          show_in_catalog: Boolean(kennels[0].show_in_catalog),
        });
      } else if (businesses.length > 0) {
        setSiteInfo({
          name: businesses[0].name,
          slug: businesses[0].slug,
          type: 'firma',
          id: businesses[0].id,
          show_in_catalog: Boolean(businesses[0].show_in_catalog),
        });
      }

      // Photos tracking
      const prof = profileRes.data || {};
      setHasPhotos(!!(prof.main_photo || (prof.gallery && prof.gallery.length > 0)));

      // Page status
      if (pageRes.data) {
        setPageStatus(pageRes.data.status || 'draft');
      }

      // Ads usage for pre-limit warning
      if (usageRes.data) {
        setAdsUsage(usageRes.data);
      }

      // WhatsApp settings
      if (waRes.data) {
        setWaSettings(waRes.data);
        if (waRes.data.phone) setWaPhone(waRes.data.phone);
      }
    }).finally(() => setLoading(false));

    // Fetch action insight separately (non-blocking)
    axios.get(`${API}/inspiration/action-insight`, { headers }).then(r => {
      if (r.data?.has_insight) {
        setActionInsight(r.data);
        trackEvent('notif_shown', { source: 'dashboard', topic: r.data.topic });
      }
    }).catch(() => {});

    // Check first-win / follow-up status
    axios.get(`${API}/user/first-win-status`, { headers }).then(r => {
      if (r.data) setFirstWinStatus(r.data);
    }).catch(() => {});

    // Check publish reminder (10-min alert)
    axios.get(`${API}/user/publish-reminder`, { headers }).then(r => {
      if (r.data?.show_reminder) setPublishReminder(r.data);
    }).catch(() => {});
  }, []); // eslint-disable-line

  // CTA Pulse — after 5s without click
  useEffect(() => {
    const timer = setTimeout(() => setCtaPulse(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleConnectFB = async () => {
    setFbConnecting(true);
    try {
      const res = await axios.get(`${API}/integrations/facebook/connect-url`, { headers });
      if (res.data?.oauth_url) {
        window.location.href = res.data.oauth_url;
      }
    } catch (err) {
      console.error('FB connect error:', err);
      setFbConnecting(false);
    }
  };

  // A/B test impression tracking
  useEffect(() => {
    if (!loading) {
      axios.post(`${API}/ab/track`, {
        test_id: 'home_cta_v1', variant: abVariant, event: 'impression',
      }, { headers }).catch(() => {});
    }
  }, [loading]); // eslint-disable-line

  useEffect(() => {
    if (!sm.loading && sm.firstLead && !sm.firstLeadCelebrated) {
      setShowFirstLead(true);
    }
  }, [sm.loading, sm.firstLead, sm.firstLeadCelebrated]);

  if (loading || sm.loading) {
    return (
      <PanelLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#F47A3B]" />
        </div>
      </PanelLayout>
    );
  }

  const handleSaveWhatsApp = async () => {
    if (!waPhone.trim()) return;
    setWaSaving(true);
    try {
      const saveRes = await axios.put(`${API}/whatsapp/settings`, { phone: waPhone, enabled: true }, { headers });
      if (saveRes.data?.ok) {
        const testRes = await axios.post(`${API}/whatsapp/test`, { phone: waPhone }, { headers });
        if (testRes.data?.ok) {
          setWaSettings({ phone: saveRes.data.phone, enabled: true, verified: true, has_number: true });
          toast.success('Gotowe — teraz każde zapytanie trafi na Twój WhatsApp');
        } else {
          toast.error(testRes.data?.error || 'Nie udało się wysłać testu');
        }
      } else {
        toast.error(saveRes.data?.error || 'Nieprawidłowy numer');
      }
    } catch {
      toast.error('Błąd zapisu numeru');
    } finally {
      setWaSaving(false);
    }
  };


  const hasProfile = sm.hasProfile;
  const fbPublished = fbPosts.filter(p => p.status === 'published');
  const fbScheduled = fbPosts.filter(p => p.status === 'scheduled');
  const nextScheduledPost = fbScheduled[0] || null;
  const fbReactions = fbPublished.reduce((s, p) => s + (p.reactions || 0), 0);
  const fbComments = fbPublished.reduce((s, p) => s + (p.comments || 0), 0);
  const fbMessages = fbPublished.reduce((s, p) => s + (p.messages || 0), 0);
  const hasFbCampaign = !!fbCampaign;
  const hasPublished = (sm.hasAnyPost && sm.postsPublished > 0) || fbPublished.length > 0;
  const hasLeads = leads.length > 0;
  const userName = user?.name?.split(' ')[0] || 'Użytkownik';
  const newLeads = leads.filter(l => l.status === 'new' || l.status === 'NEW').length;
  const totalPublished = (sm.postsPublished || 0) + fbPublished.length;

  const handlePublishNext = async () => {
    if (!nextScheduledPost || !fbCampaign) return;
    setPublishing(true);
    try {
      const res = await axios.post(
        `${API}/fb-campaign/${fbCampaign.id}/posts/${nextScheduledPost.id}/publish`,
        {},
        { headers }
      );
      if (res.data?.ok) {
        setPublishSuccess(nextScheduledPost);
        setFbPosts(prev => prev.map(p =>
          p.id === nextScheduledPost.id ? { ...p, status: 'published', fb_post_id: res.data.fb_post_id } : p
        ));
        toast.success('Reklama opublikowana na Facebooku!');
        trackEvent('one_click_publish', { post_id: nextScheduledPost.id, campaign_id: fbCampaign.id });
      } else {
        toast.error(res.data?.message || 'Nie udało się opublikować');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Błąd publikacji — spróbuj ponownie';
      toast.error(msg);
    } finally {
      setPublishing(false);
    }
  };

  // Flow steps
  const flowSteps = [
    { icon: User, label: 'Profil', done: hasProfile, active: !hasProfile },
    { icon: Send, label: 'Publikacja', done: hasPublished, active: hasProfile && !hasPublished },
    { icon: Inbox, label: 'Zapytania', done: hasLeads, active: hasPublished && !hasLeads },
    { icon: MessageCircle, label: 'Odpowiedzi', done: newLeads === 0 && hasLeads, active: newLeads > 0 },
  ];


  // Determine user state (4 states)
  const isReturning = sm.returnTriggerActive && sm.hoursAway > 48;
  const STATE = newLeads > 0 ? 'HAS_LEADS' : isReturning ? 'RETURNING' : hasPublished ? 'IN_PROGRESS' : 'NEW';

  return (
    <PanelLayout>
      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { box-shadow: 0 12px 40px rgba(244,122,59,0.25); }
          50% { box-shadow: 0 16px 50px rgba(244,122,59,0.4); }
        }
        .animate-pulse-subtle { animation: pulse-subtle 2s ease-in-out 3; }
      `}</style>
      {showFirstLead && (
        <FirstLeadCelebration onDismiss={() => { setShowFirstLead(false); sm.celebrateFirstLead(); navigate('/panel/leady'); }} />
      )}

      <div className="max-w-5xl mx-auto pt-4 px-4" data-testid="dashboard-home">

        {/* ═══ FIRST LITTER CTA (najpilniejszy nudge dla nowych hodowców) ═══ */}
        <FirstLitterPromo />

        {/* ═══ MARKETPLACE LEADS WIDGET (P0) ═══ */}
        <MarketplaceLeadsWidget authConfig={{ headers }} />

        {/* ═══ UNANSWERED LEADS ALERT ═══ */}
        {newLeads > 0 && STATE !== 'HAS_LEADS' && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 bg-[#FFF3EB] border border-[#F47A3B]/20 rounded-xl p-3 flex items-center justify-between cursor-pointer hover:bg-[#FFECE0] transition-colors"
            onClick={() => navigate('/panel/leady')}
            data-testid="unanswered-leads-alert"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#F47A3B]/15 flex items-center justify-center">
                <Inbox className="w-4 h-4 text-[#F47A3B]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-zinc-800">
                  {newLeads} {newLeads === 1 ? 'lead czeka' : 'leadów czeka'} bez odpowiedzi
                </p>
                <p className="text-[11px] text-zinc-500">Odpowiedz szybko — szybka odpowiedź = wyższa konwersja</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#F47A3B]" />
          </motion.div>
        )}
        {/* ═══════════════════════════════════════
            STATE 1: NOWY USER — ACTION HUB
            HOME = prowadzenie do efektu
            ═══════════════════════════════════════ */}
        {STATE === 'NEW' && (
          <>
            {/* ── GREETING ── */}
            <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/15 mb-4">
                <Zap className="w-3.5 h-3.5 text-[#F47A3B]" />
                <span className="text-[12px] font-semibold text-[#F47A3B]">Witaj, {userName}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 mb-2" data-testid="home-title">
                Czas na pierwszego klienta
              </h1>
              <p className="text-[14px] text-zinc-500 max-w-md mx-auto">
                Wszystko gotowe — zostało Ci opublikować post.
              </p>
            </motion.div>

            {/* ── SEKCJA 1: TWOJE PIERWSZE KROKI ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.06 }}
              className="bg-white rounded-2xl border border-zinc-200 p-5 mb-5"
              data-testid="home-status-checklist"
            >
              <p className="text-[14px] font-bold text-zinc-900 mb-1">Twoje pierwsze kroki</p>
              <p className="text-[12px] text-zinc-400 mb-4">Zrób to po kolei — poprowadzimy Cię dalej</p>
              <div className="space-y-3">
                {/* Step 1: Profil */}
                {(() => {
                  const done = hasProfile;
                  const isNext = !done;
                  return (
                    <div className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isNext ? 'bg-[#F47A3B]/5 border border-[#F47A3B]/20' : done ? 'bg-zinc-50' : 'bg-zinc-50'}`} data-testid="step-profile">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${done ? 'bg-emerald-500' : isNext ? 'bg-[#F47A3B]' : 'bg-zinc-200'}`}>
                        {done ? <Check className="w-4 h-4 text-white" /> : <span className="text-[11px] font-bold text-white">1</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13px] font-semibold ${done ? 'text-zinc-500 line-through' : 'text-zinc-900'}`}>Uzupełnij profil</p>
                        <p className="text-[11px] text-zinc-400">Nazwa, opis, telefon — to zobaczą klienci</p>
                      </div>
                      {!done && <button onClick={() => navigate('/panel/profil')} className="px-3 py-1.5 bg-[#F47A3B] text-white text-[11px] font-bold rounded-lg shrink-0 hover:bg-[#E06A2E] transition-colors">Uzupełnij</button>}
                      {done && <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />}
                    </div>
                  );
                })()}

                {/* Step 2: Zdjęcia */}
                {(() => {
                  const done = hasPhotos;
                  const isNext = hasProfile && !done;
                  return (
                    <div className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isNext ? 'bg-[#F47A3B]/5 border border-[#F47A3B]/20' : done ? 'bg-zinc-50' : 'bg-zinc-50'}`} data-testid="step-photos">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${done ? 'bg-emerald-500' : isNext ? 'bg-[#F47A3B]' : 'bg-zinc-200'}`}>
                        {done ? <Check className="w-4 h-4 text-white" /> : <span className="text-[11px] font-bold text-white">2</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13px] font-semibold ${done ? 'text-zinc-500 line-through' : isNext ? 'text-zinc-900' : 'text-zinc-400'}`}>Dodaj zdjęcia</p>
                        <p className="text-[11px] text-zinc-400">Zdjęcie główne + galeria — klienci kupują oczami</p>
                      </div>
                      {isNext && <button onClick={() => navigate('/panel/profil')} className="px-3 py-1.5 bg-[#F47A3B] text-white text-[11px] font-bold rounded-lg shrink-0 hover:bg-[#E06A2E] transition-colors">Dodaj</button>}
                      {done && <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />}
                    </div>
                  );
                })()}

                {/* Step 3: Strona */}
                {(() => {
                  const done = pageStatus === 'published';
                  const isNext = hasProfile && hasPhotos && !done;
                  return (
                    <div className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isNext ? 'bg-[#F47A3B]/5 border border-[#F47A3B]/20' : done ? 'bg-zinc-50' : 'bg-zinc-50'}`} data-testid="step-page">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${done ? 'bg-emerald-500' : isNext ? 'bg-[#F47A3B]' : 'bg-zinc-200'}`}>
                        {done ? <Check className="w-4 h-4 text-white" /> : <span className="text-[11px] font-bold text-white">3</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13px] font-semibold ${done ? 'text-zinc-500 line-through' : isNext ? 'text-zinc-900' : 'text-zinc-400'}`}>Opublikuj stronę</p>
                        <p className="text-[11px] text-zinc-400">Twoja wizytówka online — klienci trafiają tu z grup</p>
                      </div>
                      {isNext && <button onClick={() => navigate('/panel/strona')} className="px-3 py-1.5 bg-[#F47A3B] text-white text-[11px] font-bold rounded-lg shrink-0 hover:bg-[#E06A2E] transition-colors">Otwórz</button>}
                      {done && <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />}
                    </div>
                  );
                })()}

                {/* Step 4: Post */}
                {(() => {
                  const done = hasPublished;
                  const isNext = hasProfile && hasPhotos && pageStatus === 'published' && !done;
                  return (
                    <div className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isNext ? 'bg-[#F47A3B]/5 border border-[#F47A3B]/20' : done ? 'bg-zinc-50' : 'bg-zinc-50'}`} data-testid="step-publish">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${done ? 'bg-emerald-500' : isNext ? 'bg-[#F47A3B]' : 'bg-zinc-200'}`}>
                        {done ? <Check className="w-4 h-4 text-white" /> : <span className="text-[11px] font-bold text-white">4</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13px] font-semibold ${done ? 'text-zinc-500 line-through' : isNext ? 'text-zinc-900' : 'text-zinc-400'}`}>Opublikuj pierwszy post</p>
                        <p className="text-[11px] text-zinc-400">Przygotujemy post — Ty tylko klikasz „Opublikuj"</p>
                      </div>
                      {isNext && <button onClick={() => navigate('/panel/publikacja')} className="px-3 py-1.5 bg-[#F47A3B] text-white text-[11px] font-bold rounded-lg shrink-0 hover:bg-[#E06A2E] transition-colors">Opublikuj</button>}
                      {done && <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />}
                    </div>
                  );
                })()}

                {/* Step 5: Udostępnij */}
                {(() => {
                  const done = false; // Optional step, never auto-done
                  const isNext = hasPublished;
                  return isNext ? (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50/50 border border-emerald-200/50" data-testid="step-share">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 bg-emerald-500">
                        <span className="text-[11px] font-bold text-white">5</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-emerald-700">Udostępnij link klientom</p>
                        <p className="text-[11px] text-emerald-600/60">Wrzuć link do grup FB — klienci zaczną pisać</p>
                      </div>
                      <button onClick={() => navigate('/panel/strona')} className="px-3 py-1.5 bg-emerald-500 text-white text-[11px] font-bold rounded-lg shrink-0 hover:bg-emerald-600 transition-colors">Link</button>
                    </div>
                  ) : null;
                })()}
              </div>

              {/* Progress bar */}
              <div className="mt-4 pt-3 border-t border-zinc-100">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] text-zinc-400">Postęp konfiguracji</span>
                  <span className="text-[11px] font-bold text-[#F47A3B]">
                    {[hasProfile, hasPhotos, pageStatus === 'published', hasPublished].filter(Boolean).length}/4
                  </span>
                </div>
                <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#F47A3B] to-[#F9A66C] transition-all duration-500"
                    style={{ width: `${([hasProfile, hasPhotos, pageStatus === 'published', hasPublished].filter(Boolean).length / 4) * 100}%` }}
                  />
                </div>
              </div>
            </motion.div>

            {/* ── SEKCJA 2: GŁÓWNE CTA + POST PREVIEW ── */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
              className="mb-5"
            >
              {/* Post preview snippet */}
              {postPreview && (
                <div
                  className="mb-3 p-3.5 rounded-xl bg-white border border-zinc-200 cursor-pointer hover:border-[#F47A3B]/30 transition-all"
                  onClick={() => navigate('/panel/publikacja')}
                  data-testid="post-preview"
                >
                  <p className="text-[10px] text-[#F47A3B] font-bold uppercase tracking-wider mb-1.5">Twój post jest gotowy</p>
                  <p className="text-[13px] text-zinc-700 leading-relaxed line-clamp-3">
                    {postPreview.slice(0, 160)}{postPreview.length > 160 ? '...' : ''}
                  </p>
                  <p className="text-[11px] text-[#F47A3B] font-semibold mt-2 flex items-center gap-1">
                    Zobacz całość <ArrowRight className="w-3 h-3" />
                  </p>
                </div>
              )}

              {/* Mini readiness checklist */}
              <div className="flex items-center justify-center gap-4 mb-3" data-testid="mini-checklist">
                <span className={`flex items-center gap-1.5 text-[12px] font-medium ${hasProfile ? 'text-emerald-600' : 'text-zinc-400'}`}>
                  <CheckCircle2 className={`w-3.5 h-3.5 ${hasProfile ? 'text-emerald-500' : 'text-zinc-300'}`} />
                  Post gotowy
                </span>
                <span className={`flex items-center gap-1.5 text-[12px] font-medium ${siteInfo ? 'text-emerald-600' : 'text-zinc-400'}`}>
                  <CheckCircle2 className={`w-3.5 h-3.5 ${siteInfo ? 'text-emerald-500' : 'text-zinc-300'}`} />
                  Strona aktywna
                </span>
              </div>

              {/* Persuasion text above CTA */}
              <p className="text-center text-[13px] text-zinc-600 font-medium mb-3" data-testid="cta-persuasion-above">
                Twój post jest gotowy — wystarczy opublikować
              </p>

              <motion.button
                whileHover={{ scale: 1.01, y: -2 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => {
                  axios.post(`${API}/ab/track`, {
                    test_id: 'home_cta_v1', variant: abVariant, event: 'cta_click',
                  }, { headers }).catch(() => {});
                  trackEvent('home_cta_click', { variant: abVariant });
                  navigate('/panel/publikacja');
                }}
                className={`w-full rounded-2xl p-6 text-left transition-all group relative overflow-hidden ${ctaPulse ? 'animate-pulse-subtle' : ''}`}
                style={{
                  background: 'linear-gradient(135deg, #F47A3B 0%, #E06A2E 100%)',
                  boxShadow: ctaPulse
                    ? '0 12px 50px rgba(244,122,59,0.35)'
                    : '0 12px 40px rgba(244,122,59,0.25)',
                }}
                data-testid="home-main-cta"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center shrink-0 group-hover:bg-white/20 transition-colors">
                    <Send className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[18px] font-extrabold text-white mb-1" data-testid="cta-text">
                      {postPreview
                        ? 'Zobacz i opublikuj'
                        : abVariant === 'B'
                        ? 'Uruchom post i zdobądź pierwsze zapytania'
                        : 'Opublikuj pierwszy post'}
                    </p>
                    <p className="text-[13px] text-white/70">
                      {abVariant === 'B'
                        ? 'Przygotujemy post za Ciebie — Ty tylko klikasz'
                        : 'Przygotujemy post za Ciebie — Ty tylko klikasz'}
                    </p>
                  </div>
                  <ArrowRight className="w-6 h-6 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all shrink-0" />
                </div>
              </motion.button>

              {/* Persuasion text below CTA */}
              <p className="text-center text-[12px] text-zinc-400 mt-2.5" data-testid="micro-motivation">
                Nie potrzebujesz żadnej wiedzy — wszystko gotowe
              </p>
              <p className="text-center text-[11px] text-zinc-400/70 mt-1" data-testid="urgency-text">
                Im szybciej opublikujesz, tym szybciej pojawią się zapytania
              </p>

              {/* System generator counter */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-4 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#F47A3B]/5 border border-[#F47A3B]/15"
                data-testid="system-counter"
              >
                <TrendingUp className="w-4 h-4 text-[#F47A3B] flex-shrink-0" />
                <p className="text-[13px] font-bold text-[#F47A3B]">Możesz oczekiwać: ~5–15 zapytań / miesiąc</p>
              </motion.div>
            </motion.div>

            {/* ── "TWOJA STRONA JEST AKTYWNA" ── */}
            {siteInfo && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white rounded-2xl border border-zinc-200 p-4 mb-5 flex items-center justify-between"
                data-testid="site-active-banner"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <Globe2 className="w-4.5 h-4.5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-zinc-900">Twoja strona jest aktywna</p>
                    <p className="text-[11px] text-zinc-400 font-mono">{siteInfo.slug}.apisai.pl</p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/panel/strona')}
                  variant="outline"
                  size="sm"
                  className="rounded-xl h-8 px-3 text-[11px] font-semibold border-zinc-200 hover:border-emerald-300"
                  data-testid="site-open-btn"
                >
                  <ExternalLink className="w-3 h-3 mr-1" /> Otwórz
                </Button>
              </motion.div>
            )}

            {/* ── WIDOCZNOŚĆ W KATALOGU APISAI ── */}
            {siteInfo && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.17 }}
                className={`rounded-2xl border p-4 mb-5 ${
                  siteInfo.show_in_catalog
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-amber-50 border-amber-200'
                }`}
                data-testid="catalog-visibility-banner"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      siteInfo.show_in_catalog ? 'bg-emerald-500/15' : 'bg-amber-500/15'
                    }`}>
                      {siteInfo.show_in_catalog
                        ? <CheckCircle2 className="w-4.5 h-4.5 text-emerald-600" />
                        : <EyeOff className="w-4.5 h-4.5 text-amber-600" />
                      }
                    </div>
                    <div className="min-w-0">
                      <p className={`text-[13px] font-semibold ${
                        siteInfo.show_in_catalog ? 'text-emerald-900' : 'text-amber-900'
                      }`}>
                        {siteInfo.show_in_catalog
                          ? 'Widoczna w katalogu ApisAI'
                          : 'Ukryta w katalogu ApisAI'}
                      </p>
                      <p className={`text-[11px] ${
                        siteInfo.show_in_catalog ? 'text-emerald-700' : 'text-amber-700'
                      }`}>
                        {siteInfo.show_in_catalog
                          ? 'Klienci mogą Cię znaleźć w Google przez apisai.pl/katalog'
                          : 'Włącz aby pojawiać się w wynikach Google'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      const newVal = !siteInfo.show_in_catalog;
                      try {
                        const endpoint = siteInfo.type === 'hodowla'
                          ? `${API}/kennels/${siteInfo.id}/catalog-visibility`
                          : `${API}/my/business/catalog-visibility`;
                        await axios.patch(endpoint, { show_in_catalog: newVal }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                        setSiteInfo({ ...siteInfo, show_in_catalog: newVal });
                        toast.success(newVal ? 'Pokazujemy Cię w katalogu!' : 'Ukryto z katalogu');
                      } catch (e) {
                        toast.error('Błąd — spróbuj ponownie');
                      }
                    }}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${
                      siteInfo.show_in_catalog ? 'bg-emerald-500' : 'bg-zinc-300'
                    }`}
                    data-testid="catalog-visibility-toggle"
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      siteInfo.show_in_catalog ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </motion.div>
            )}

            {/* ── SEKCJA 3: CO TERAZ — dynamiczne kroki ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.18 }}
              className="bg-white rounded-2xl border border-zinc-200 p-5 mb-5"
              data-testid="home-next-steps"
            >
              <p className="text-[11px] text-zinc-400 font-semibold uppercase tracking-wider mb-3">Co teraz</p>
              <div className="space-y-1">
                {[
                  {
                    num: '1',
                    label: 'Opublikuj post',
                    desc: 'Przygotujemy go za Ciebie',
                    active: !hasPublished,
                    done: hasPublished,
                    action: () => navigate('/panel/publikacja'),
                  },
                  {
                    num: '2',
                    label: 'Udostępnij stronę',
                    desc: 'Klienci znajdą Cię w Google',
                    active: hasPublished && !hasLeads,
                    done: hasLeads,
                    action: () => navigate('/panel/strona'),
                  },
                  {
                    num: '3',
                    label: 'Czekaj na zapytania',
                    desc: 'Pierwsze pojawiają się w 24–72h',
                    active: hasPublished,
                    done: hasLeads,
                    action: () => navigate('/panel/leady'),
                  },
                ].map((step, i) => (
                  <motion.button
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.22 + i * 0.06 }}
                    onClick={step.action}
                    disabled={step.done && !step.active}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                      step.active && !step.done
                        ? 'bg-[#F47A3B]/5 hover:bg-[#F47A3B]/8 cursor-pointer'
                        : step.done
                        ? 'opacity-50'
                        : 'hover:bg-zinc-50 cursor-pointer'
                    }`}
                    data-testid={`next-step-${i}`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[12px] font-bold ${
                      step.done
                        ? 'bg-emerald-500 text-white'
                        : step.active && !step.done
                        ? 'bg-[#F47A3B] text-white'
                        : 'bg-zinc-100 text-zinc-400'
                    }`}>
                      {step.done ? <Check className="w-3.5 h-3.5" /> : step.num}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[13px] font-medium ${step.done ? 'text-zinc-400 line-through' : 'text-zinc-900'}`}>
                        {step.label}
                      </p>
                      <p className="text-[11px] text-zinc-400">{step.desc}</p>
                    </div>
                    {!step.done && <ChevronRight className="w-4 h-4 text-zinc-300 shrink-0" />}
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* ── SEKCJA 4: FEEDBACK ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-center p-4 rounded-2xl mb-2"
              style={{ background: 'linear-gradient(135deg, #F0FDF4 0%, #F8FAFC 100%)', border: '1px solid rgba(16,185,129,0.12)' }}
              data-testid="home-feedback"
            >
              <div className="flex items-center justify-center gap-2 mb-1.5">
                <Clock className="w-3.5 h-3.5 text-emerald-500" />
                <p className="text-[13px] font-semibold text-zinc-800">
                  Pierwsze zapytania pojawiają się zwykle w 24–72h
                </p>
              </div>
              <p className="text-[11px] text-zinc-500">
                Po opublikowaniu posta my zajmiemy się resztą
              </p>
            </motion.div>

            {/* ── MINI DOWÓD SPOŁECZNY ── */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35 }}
              className="text-center text-[11px] text-emerald-600/70 mb-5"
              data-testid="social-proof"
            >
              Inni hodowcy już dostają pierwsze wiadomości
            </motion.p>

            {/* Multi-breed CTA — subtle */}
            {breedCount <= 1 && user?.profile_type !== 'biznes' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="p-3.5 rounded-xl flex items-center justify-between mb-4"
                style={{ background: 'rgba(244,122,59,0.04)', border: '1px solid rgba(244,122,59,0.1)' }}
                data-testid="multi-breed-cta"
              >
                <div className="flex items-center gap-2.5">
                  <Dog className="w-4 h-4 text-[#F47A3B]/60" />
                  <p className="text-[12px] text-zinc-600">Masz więcej ras? <button onClick={() => navigate('/panel/profil')} className="text-[#F47A3B] font-semibold hover:underline">Dodaj je</button></p>
                </div>
              </motion.div>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════
            STATE 2: W TRAKCIE — 1-CLICK PUBLISH MODE
            ═══════════════════════════════════════ */}
        {STATE === 'IN_PROGRESS' && (
          <>
            {/* ── SUCCESS STATE — just published ── */}
            {publishSuccess && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center py-16 text-center"
                data-testid="publish-success"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                  className="w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/30"
                >
                  <Check className="w-10 h-10 text-white" strokeWidth={3} />
                </motion.div>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 mb-2">Opublikowano!</h1>
                <p className="text-[15px] text-zinc-500 mb-6 max-w-sm">
                  Tak zaczynają się zapytania. Reklama jest już na Facebooku — teraz zbieramy reakcje.
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setPublishSuccess(null)}
                    className="bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-xl px-6 py-3 text-[14px] font-bold"
                    data-testid="publish-success-back-btn"
                  >
                    Wróć do panelu
                  </Button>
                  <Button
                    onClick={() => navigate('/panel/kampania')}
                    variant="outline"
                    className="rounded-xl px-6 py-3 text-[14px] font-semibold border-zinc-200"
                    data-testid="publish-success-campaign-btn"
                  >
                    Zobacz kampanię
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ── MAIN 1-CLICK MODE ── */}
            {!publishSuccess && (
              <>
                {/* Header */}
                <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/15 mb-4">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    <span className="text-[12px] font-semibold text-emerald-600">Promocja aktywna</span>
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 mb-2" data-testid="home-title">
                    {nextScheduledPost
                      ? 'Twoja reklama czeka na publikację'
                      : 'Wszystko gotowe — czekaj na zapytania'}
                  </h1>
                  <p className="text-[14px] text-zinc-500 max-w-lg mx-auto">
                    {nextScheduledPost
                      ? 'Jeden klik — i jest na Facebooku.'
                      : `${fbPublished.length} opublikowanych · ${fbScheduled.length} gotowych`}
                  </p>
                </motion.div>

                {/* ═══ THE BIG BUTTON ═══ */}
                {nextScheduledPost && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08, type: "spring", stiffness: 120 }}
                    className="flex flex-col items-center justify-center py-8 mb-6"
                    data-testid="one-click-publish-section"
                  >
                    {/* Post preview snippet */}
                    {nextScheduledPost.text && (
                      <div className="w-full max-w-md mb-6 p-4 rounded-2xl bg-white border border-zinc-200 shadow-sm" data-testid="next-post-preview">
                        {nextScheduledPost.image_url && nextScheduledPost.image_url !== 'None' && nextScheduledPost.image_url !== 'null' && (
                          <img
                            src={nextScheduledPost.image_url.startsWith('/uploads') ? `/api${nextScheduledPost.image_url}` : nextScheduledPost.image_url}
                            alt="Reklama"
                            className="w-full h-40 object-cover rounded-xl mb-3"
                          />
                        )}
                        <p className="text-[13px] text-zinc-600 leading-relaxed line-clamp-3">
                          {nextScheduledPost.text.slice(0, 180)}{nextScheduledPost.text.length > 180 ? '...' : ''}
                        </p>
                        <p className="text-[11px] text-zinc-400 mt-2">
                          Reklama {fbPublished.length + 1} z {fbPosts.length}
                        </p>
                      </div>
                    )}

                    <motion.button
                      whileHover={{ scale: 1.03, y: -3 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={handlePublishNext}
                      disabled={publishing}
                      className="relative rounded-2xl px-12 py-7 text-center transition-all disabled:opacity-70"
                      style={{
                        background: 'linear-gradient(135deg, #F47A3B 0%, #E06A2E 100%)',
                        boxShadow: '0 16px 50px rgba(244,122,59,0.35)',
                      }}
                      data-testid="one-click-publish-btn"
                    >
                      {publishing ? (
                        <div className="flex items-center gap-3">
                          <Loader2 className="w-8 h-8 text-white animate-spin" />
                          <span className="text-2xl font-extrabold text-white">Publikuję...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-4">
                          <Send className="w-8 h-8 text-white" />
                          <span className="text-2xl sm:text-3xl font-extrabold text-white">
                            Opublikuj reklamę
                          </span>
                        </div>
                      )}
                    </motion.button>
                    <p className="mt-4 text-[14px] text-zinc-400">
                      Publikacja zajmie 1 klik — post trafi na Facebook.
                    </p>
                  </motion.div>
                )}

                {/* ═══ NO SCHEDULED POSTS — Generate CTA ═══ */}
                {!nextScheduledPost && hasFbCampaign && (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="flex flex-col items-center justify-center py-8 mb-6"
                    data-testid="no-scheduled-section"
                  >
                    <div className="w-16 h-16 rounded-full bg-zinc-100 flex items-center justify-center mb-4">
                      <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                    </div>
                    <p className="text-[16px] font-bold text-zinc-900 mb-1">Wszystkie reklamy opublikowane!</p>
                    <p className="text-[14px] text-zinc-500 mb-6">{fbPublished.length} z {fbPosts.length} reklam jest już na Facebooku.</p>
                    <Button
                      onClick={() => navigate('/panel/kreator-reklam')}
                      className="bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-2xl px-8 py-4 text-[16px] font-bold shadow-lg shadow-[#F47A3B]/25"
                      data-testid="generate-ad-btn"
                    >
                      <Wand2 className="w-5 h-5 mr-2" /> Wygeneruj nową reklamę
                    </Button>
                  </motion.div>
                )}

                {/* ═══ PRE-LIMIT WARNING ═══ */}
                {adsUsage && adsUsage.remaining <= 5 && adsUsage.remaining > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.12 }}
                    className="mb-4 p-4 rounded-2xl border-2 border-amber-300 bg-amber-50"
                    data-testid="pre-limit-warning"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-amber-400 flex items-center justify-center shrink-0">
                        <AlertCircle className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-[14px] font-bold text-zinc-900">
                          Zostało {adsUsage.remaining} {adsUsage.remaining === 1 ? 'reklama' : adsUsage.remaining <= 4 ? 'reklamy' : 'reklam'} — nie zatrzymuj się
                        </p>
                        <p className="text-[12px] text-zinc-500 mt-0.5">Dokup pakiet, żeby dalej publikować bez przerwy.</p>
                      </div>
                      <Button
                        onClick={() => navigate('/panel/ustawienia?tab=plan')}
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl px-4 text-[12px] font-bold shrink-0"
                        data-testid="pre-limit-topup-btn"
                      >
                        Dokup
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* ═══ COMPACT STATUS BAR ═══ */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="bg-white rounded-2xl border border-zinc-200 p-4 mb-5"
                  data-testid="home-status-compact"
                >
                  <div className="grid grid-cols-3 divide-x divide-zinc-100">
                    <div className="text-center px-3">
                      <p className="text-[20px] font-extrabold text-zinc-900">{fbPublished.length}<span className="text-[12px] text-zinc-400">/{fbPosts.length}</span></p>
                      <p className="text-[10px] text-zinc-400 font-medium">Opublikowane</p>
                    </div>
                    <div className="text-center px-3">
                      <p className="text-[20px] font-extrabold text-zinc-900">{fbScheduled.length}</p>
                      <p className="text-[10px] text-zinc-400 font-medium">Gotowe do pub.</p>
                    </div>
                    <div className="text-center px-3">
                      <p className="text-[20px] font-extrabold text-zinc-900">{leads.length}</p>
                      <p className="text-[10px] text-zinc-400 font-medium">Zapytania</p>
                    </div>
                  </div>
                </motion.div>

                {/* ═══ QUICK LINKS ═══ */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.18 }}
                  className="grid grid-cols-2 gap-2 mb-5"
                >
                  <button
                    onClick={() => navigate('/panel/kampania')}
                    className="flex items-center gap-2 p-3 rounded-xl bg-white border border-zinc-200 hover:border-[#F47A3B] hover:bg-orange-50/30 transition-all text-left"
                    data-testid="quick-link-campaign"
                  >
                    <Rocket className="w-4 h-4 text-[#F47A3B]" />
                    <span className="text-[12px] font-medium text-zinc-700">Publikuj &rarr; post trafi na Facebook</span>
                    <ChevronRight className="w-3 h-3 text-zinc-300 ml-auto" />
                  </button>
                  <button
                    onClick={() => navigate('/panel/leady')}
                    className="flex items-center gap-2 p-3 rounded-xl bg-white border border-zinc-200 hover:border-zinc-300 transition-all text-left"
                    data-testid="quick-link-kreator"
                  >
                    <MessageCircle className="w-4 h-4 text-[#F47A3B]" />
                    <span className="text-[12px] font-medium text-zinc-700">Tu piszą klienci</span>
                    <ChevronRight className="w-3 h-3 text-zinc-300 ml-auto" />
                  </button>
                </motion.div>

                {/* ═══ SITE ACTIVE BANNER ═══ */}
                {siteInfo && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-2xl border border-zinc-200 p-4 mb-5 flex items-center justify-between"
                    data-testid="site-active-banner"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                        <Globe2 className="w-4.5 h-4.5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-[13px] font-semibold text-zinc-900">Twoja strona jest aktywna</p>
                        <p className="text-[11px] text-zinc-400 font-mono">{siteInfo.slug}.apisai.pl</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => navigate('/panel/strona')}
                      variant="outline"
                      size="sm"
                      className="rounded-xl h-8 px-3 text-[11px] font-semibold border-zinc-200 hover:border-emerald-300"
                      data-testid="site-open-btn"
                    >
                      <ExternalLink className="w-3 h-3 mr-1" /> Otwórz
                    </Button>
                  </motion.div>
                )}

                {/* ── FB CONNECTION BANNER ── */}
                {fbConnected === false && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.22 }}
                    className="rounded-2xl border-2 border-blue-200 bg-blue-50 p-5 mb-4"
                    data-testid="fb-connect-banner"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center shrink-0">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-bold text-zinc-900 mb-1">Połącz Facebooka</h4>
                        <p className="text-xs text-zinc-500 mb-3">Podłącz swoją stronę, żeby publikować reklamy bezpośrednio na Facebooku</p>
                        <button
                          onClick={handleConnectFB}
                          disabled={fbConnecting}
                          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-colors"
                          data-testid="fb-connect-btn"
                        >
                          {fbConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                          )}
                          {fbConnecting ? 'Łączenie...' : 'Połącz z Facebookiem'}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* FB connected badge */}
                {fbConnected === true && fbPageName && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.22 }}
                    className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 mb-4"
                    data-testid="fb-connected-badge"
                  >
                    <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    </div>
                    <p className="text-xs font-bold text-emerald-800 truncate flex-1">Facebook połączony — {fbPageName}</p>
                    <button onClick={handleConnectFB} className="text-[10px] text-zinc-400 hover:text-zinc-600 underline shrink-0" data-testid="fb-change-page-btn">Zmień</button>
                  </motion.div>
                )}

                {/* ═══ WHATSAPP SETUP ALERT ═══ */}
                {waSettings && !waSettings.has_number && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.23 }}
                    className="rounded-2xl border-2 border-[#25D366]/30 bg-[#25D366]/5 p-5 mb-4"
                    data-testid="whatsapp-setup-alert"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#25D366] flex items-center justify-center shrink-0">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-[14px] font-bold text-zinc-900 mb-1">Dodaj numer WhatsApp</p>
                        <p className="text-[12px] text-zinc-500 mb-3">Dostaniesz powiadomienie na telefon gdy klient wyśle zapytanie. Nie przegapisz żadnego leada.</p>
                        <div className="flex items-center gap-2">
                          <input
                            type="tel"
                            value={waPhone}
                            onChange={e => setWaPhone(e.target.value)}
                            placeholder="+48 500 000 000"
                            className="flex-1 px-3 py-2 rounded-xl border border-zinc-200 text-[13px] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] outline-none"
                            data-testid="whatsapp-phone-input"
                          />
                          <Button
                            onClick={handleSaveWhatsApp}
                            disabled={waSaving || !waPhone.trim()}
                            size="sm"
                            className="bg-[#25D366] hover:bg-[#1DA851] text-white rounded-xl px-4 text-[12px] font-bold shrink-0"
                            data-testid="whatsapp-save-btn"
                          >
                            {waSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Zapisz i testuj'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* WhatsApp verified badge */}
                {waSettings?.has_number && waSettings?.verified && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.23 }}
                    className="flex items-center gap-3 p-3 rounded-xl bg-[#25D366]/5 border border-[#25D366]/20 mb-4"
                    data-testid="whatsapp-verified-badge"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#25D366] flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4 text-white" strokeWidth={3} />
                    </div>
                    <p className="text-[12px] font-bold text-[#25D366] truncate flex-1">WhatsApp aktywny — będziesz dostawać zapytania tutaj</p>
                  </motion.div>
                )}

                {/* FEEDBACK — no leads yet */}
                {leads.length === 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="text-center p-4 rounded-2xl mb-2"
                    style={{ background: 'linear-gradient(135deg, #F0FDF4 0%, #F8FAFC 100%)', border: '1px solid rgba(16,185,129,0.12)' }}
                    data-testid="home-feedback"
                  >
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Clock className="w-3.5 h-3.5 text-emerald-500" />
                      <p className="text-[13px] font-semibold text-zinc-800">Pierwsze zapytania pojawiają się w 24–72h</p>
                    </div>
                    <p className="text-[11px] text-zinc-500">Twoja oferta jest widoczna — czekaj na zapytania</p>
                  </motion.div>
                )}

                {/* Multi-breed CTA */}
                {breedCount <= 1 && user?.profile_type !== 'biznes' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.28 }}
                    className="p-3.5 rounded-xl flex items-center justify-between mb-4"
                    style={{ background: 'rgba(244,122,59,0.04)', border: '1px solid rgba(244,122,59,0.1)' }}
                    data-testid="multi-breed-cta"
                  >
                    <div className="flex items-center gap-2.5">
                      <Dog className="w-4 h-4 text-[#F47A3B]/60" />
                      <p className="text-[12px] text-zinc-600">Masz więcej ras? <button onClick={() => navigate('/panel/profil')} className="text-[#F47A3B] font-semibold hover:underline">Dodaj je</button></p>
                    </div>
                  </motion.div>
                )}

                {/* Wyniki */}
                <WeeklyReportCard />
                <MyTrendsBlock />
              </>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════
            STATE 3: MA LEADY — reakcja i sprzedaż
            ═══════════════════════════════════════ */}
        {STATE === 'HAS_LEADS' && (
          <>
            <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/15 mb-4">
                <Inbox className="w-3.5 h-3.5 text-[#F47A3B]" />
                <span className="text-[12px] font-semibold text-[#F47A3B]">{newLeads} {newLeads === 1 ? 'nowe zapytanie' : 'nowe zapytania'}</span>
              </div>
              <h1 className="text-3xl font-extrabold text-zinc-900 mb-3" data-testid="home-title">
                Masz nowe zapytania od klientów
              </h1>
              <p className="text-[15px] text-zinc-500 max-w-md mx-auto">
                Odpowiedz szybko, aby zwiększyć szansę na sprzedaż.
              </p>
            </motion.div>

            {/* Lead cards */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-3 mb-6" data-testid="home-leads">
              {leads.slice(0, 3).map((lead, i) => (
                <motion.div
                  key={lead.id || i}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.08 }}
                  className="bg-white rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:bg-zinc-50 transition-colors"
                  onClick={() => navigate('/panel/leady')}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-[14px] ${
                    (lead.status === 'new' || lead.status === 'NEW') ? 'bg-[#F47A3B]' : 'bg-zinc-300'
                  }`}>
                    {(lead.name || lead.sender_name || '?')[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-semibold text-zinc-900 truncate">{lead.name || lead.sender_name || 'Klient'}</p>
                    <p className="text-[12px] text-zinc-400 truncate">{lead.message || lead.email || 'Zapytanie o ofertę'}</p>
                  </div>
                  {(lead.status === 'new' || lead.status === 'NEW') && (
                    <span className="px-2 py-0.5 rounded-full bg-[#F47A3B]/10 text-[10px] font-bold text-[#F47A3B]">NOWE</span>
                  )}
                  <ChevronRight className="w-4 h-4 text-zinc-300" />
                </motion.div>
              ))}
            </motion.div>

            {/* ═══ 1-CLICK PUBLISH in HAS_LEADS state ═══ */}
            {nextScheduledPost && !publishSuccess && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.14 }}
                className="mb-5 p-4 rounded-2xl border-2 border-[#F47A3B]/30 bg-[#FFF7ED]"
                data-testid="one-click-publish-leads"
              >
                <div className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-bold text-zinc-900 mb-0.5">Masz gotową reklamę</p>
                    <p className="text-[12px] text-zinc-500">Reklama {fbPublished.length + 1} z {fbPosts.length} — jeden klik i jest na FB</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handlePublishNext}
                    disabled={publishing}
                    className="shrink-0 rounded-xl px-5 py-3 text-white font-bold text-[13px]"
                    style={{ background: 'linear-gradient(135deg, #F47A3B 0%, #E06A2E 100%)' }}
                    data-testid="one-click-publish-btn-leads"
                  >
                    {publishing ? (
                      <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Publikuję...</span>
                    ) : (
                      <span className="flex items-center gap-2"><Send className="w-4 h-4" /> Opublikuj</span>
                    )}
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* System activity + Sales — shown when user has leads */}

            {/* WhatsApp setup alert in HAS_LEADS */}
            {waSettings && !waSettings.has_number && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.16 }}
                className="rounded-2xl border-2 border-[#25D366]/30 bg-[#25D366]/5 p-5 mb-4"
                data-testid="whatsapp-setup-alert-leads"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#25D366] flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-[14px] font-bold text-zinc-900 mb-1">Nie przegap kolejnych zapytań!</p>
                    <p className="text-[12px] text-zinc-500 mb-3">Dodaj numer WhatsApp — dostaniesz powiadomienie na telefon.</p>
                    <div className="flex items-center gap-2">
                      <input type="tel" value={waPhone} onChange={e => setWaPhone(e.target.value)} placeholder="+48 500 000 000" className="flex-1 px-3 py-2 rounded-xl border border-zinc-200 text-[13px] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] outline-none" data-testid="whatsapp-phone-input-leads" />
                      <Button onClick={handleSaveWhatsApp} disabled={waSaving || !waPhone.trim()} size="sm" className="bg-[#25D366] hover:bg-[#1DA851] text-white rounded-xl px-4 text-[12px] font-bold shrink-0" data-testid="whatsapp-save-btn-leads">
                        {waSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Zapisz i testuj'}
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {systemActivity && systemActivity.posts_published > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.18 }}
                className="rounded-2xl border border-zinc-200 bg-white p-4 mb-4"
                data-testid="system-activity-block-leads"
              >
                <p className="text-[13px] font-bold text-zinc-900 mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-[#F47A3B]" /> Twoje reklamy działają
                </p>
                <div className="flex items-center gap-3 text-[12px] text-zinc-600 flex-wrap">
                  <span className="flex items-center gap-1"><Send className="w-3 h-3 text-emerald-500" /> Przygotowaliśmy <b>{systemActivity.posts_published}</b> {systemActivity.posts_published === 1 ? 'post' : 'postów'}</span>
                  {systemActivity.groups_count > 0 && (
                    <span className="flex items-center gap-1"><Users className="w-3 h-3 text-blue-500" /> Opublikowano w <b>{systemActivity.groups_count}</b> grupach</span>
                  )}
                  {systemActivity.views_estimate > 0 && (
                    <span className="flex items-center gap-1"><BarChart3 className="w-3 h-3 text-amber-500" /> Twoją ofertę zobaczyło ~<b>{systemActivity.views_estimate}</b> osób</span>
                  )}
                </div>
                <p className="text-[11px] text-zinc-400 mt-2">To z tych działań przyszły Twoje zapytania</p>
              </motion.div>
            )}

            {salesSummary && salesSummary.clients_count > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4 mb-6"
                data-testid="home-sales-summary-leads"
              >
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <p className="text-[13px] font-bold text-emerald-800 mb-0.5">Twoje wyniki sprzedaży</p>
                    <p className="text-[11px] text-emerald-600">
                      {salesSummary.recent_clients?.[0]?.utm_campaign
                        ? <>Publikacja <b>{salesSummary.recent_clients[0].utm_campaign}</b> dała Ci klienta</>
                        : 'Klienci pozyskani dzięki reklamom'}
                    </p>
                  </div>
                  <div className="flex items-center gap-5">
                    <div className="text-center">
                      <p className="text-[22px] font-extrabold text-emerald-700">{salesSummary.clients_count}</p>
                      <p className="text-[10px] text-emerald-500 font-medium">{salesSummary.clients_count === 1 ? 'klient' : 'klientów'}</p>
                    </div>
                    {salesSummary.total_value > 0 && (
                      <div className="text-center">
                        <p className="text-[22px] font-extrabold text-emerald-700">{salesSummary.total_value.toLocaleString('pl-PL')}<span className="text-[14px] ml-0.5">zł</span></p>
                        <p className="text-[10px] text-emerald-500 font-medium">zarobione</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Ostatnia inspiracja — HAS_LEADS */}
            {lastInspiration && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.22 }}
                className="rounded-2xl border border-[#F47A3B]/15 bg-[#F47A3B]/5 p-4 mb-4 cursor-pointer hover:border-[#F47A3B]/30 transition-colors"
                onClick={() => navigate('/panel/competitor-watch')}
                data-testid="last-inspiration-widget-leads"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#F47A3B]/10 flex items-center justify-center shrink-0">
                    <Sparkles className="w-5 h-5 text-[#F47A3B]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-bold text-zinc-900 truncate">
                      Inspiracja: {lastInspiration.analysis?.topic || 'Nowy post'}
                    </p>
                    <p className="text-[11px] text-zinc-500 truncate mt-0.5">
                      {lastInspiration.generated_post ? lastInspiration.generated_post.slice(0, 80) + '...' : 'Wygeneruj post z inspiracji'}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#F47A3B] shrink-0" />
                </div>
              </motion.div>
            )}

            {/* Twoje wyniki — HAS_LEADS */}
            <WeeklyReportCard />
            <MyTrendsBlock />

            {/* CTA */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="text-center" data-testid="home-action">
              <Button
                onClick={() => navigate('/panel/leady')}
                className="bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-full px-10 py-4 text-[16px] font-bold shadow-lg shadow-[#F47A3B]/25 hover:shadow-xl hover:shadow-[#F47A3B]/30 transition-all"
                data-testid="home-main-cta"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Odpowiedz klientowi
              </Button>
              <p className="text-[12px] text-zinc-400 mt-3">Szybka odpowiedź zwiększa szansę sprzedaży nawet o 50%</p>
            </motion.div>
          </>
        )}

        {/* ═══════════════════════════════════════
            STATE 4: WRACA PO CZASIE — reaktywacja
            ═══════════════════════════════════════ */}
        {STATE === 'RETURNING' && (
          <>
            <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/15 mb-4">
                <Clock className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-[12px] font-semibold text-amber-600">Witaj ponownie, {userName}</span>
              </div>
              <h1 className="text-3xl font-extrabold text-zinc-900 mb-3" data-testid="home-title">
                Warto wrócić do działania
              </h1>
              <p className="text-[15px] text-zinc-500 max-w-lg mx-auto">
                Masz nowe aktywności do sprawdzenia — publikacje, zapytania lub odpowiedzi wymagające reakcji.
              </p>
            </motion.div>

            {/* Status summary */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl p-5 mb-6" data-testid="home-status">
              <div className="flex items-center divide-x divide-zinc-100">
                <StatusMetric label="Publikacje" value={sm.postsPublished} color={sm.postsPublished > 0 ? 'green' : 'zinc'} />
                <StatusMetric label="Zapytania" value={leads.length} color={leads.length > 0 ? 'orange' : 'zinc'} />
                <StatusMetric label="Nieobecność" value={`${Math.floor(sm.hoursAway / 24)} dni`} color="orange" />
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-center mb-8" data-testid="home-action">
              <Button
                onClick={() => navigate(leads.length > 0 ? '/panel/leady' : '/panel/publikacja')}
                className="bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-full px-10 py-4 text-[16px] font-bold shadow-lg shadow-[#F47A3B]/25 hover:shadow-xl hover:shadow-[#F47A3B]/30 transition-all"
                data-testid="home-main-cta"
              >
                <ArrowRight className="w-5 h-5 mr-2" />
                Sprawdź teraz
              </Button>
              <p className="text-[12px] text-zinc-400 mt-3">Regularna aktywność utrzymuje Twoją widoczność</p>
            </motion.div>
          </>
        )}

      </div>
    </PanelLayout>
  );
}
